import jsPDF from "jspdf";
import { supabase } from "../../lib/supabase";
import { logActivity } from "./driveClient";

// PDF-Header-Bild: neues Kombi-Logo (größer, ohne Balken)
const PDF_LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_neon-beats-label/artifacts/as4oc6zo_INDEPENDENT_MUSIC_LABEL__1_-removebg-preview.png";

// ═══════════════════════════════════════════════════════════════
// Logo Loader (cached, converts remote PNG → data URL)
// ═══════════════════════════════════════════════════════════════
let _logoCache = null;
async function loadLogoDataUrl() {
  if (_logoCache) return _logoCache;
  try {
    const resp = await fetch(PDF_LOGO_URL, { mode: "cors" });
    const blob = await resp.blob();
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
    _logoCache = dataUrl;
    return dataUrl;
  } catch (e) {
    console.warn("Logo konnte nicht geladen werden:", e);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════
// Placeholder Replacement
// ═══════════════════════════════════════════════════════════════

export function extractPlaceholders(template) {
  const matches = template.matchAll(/\{\{([a-zA-Z0-9_]+)\}\}/g);
  const set = new Set();
  for (const m of matches) set.add(m[1]);
  return Array.from(set);
}

export function renderTemplate(template, values) {
  return template.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (_m, key) => {
    const v = values[key];
    if (v === null || v === undefined || v === "") return `[${key}]`;
    return String(v);
  });
}

// Build value bag from an artist / customer / external / feature / creator row
export function buildValues({ artist, customer, external, feature, creator, custom = {} }) {
  const today = new Date().toLocaleDateString("de-DE");
  const base = {
    contract_date: today,
    original_contract_date: today,
    // Feature-Vertrag Defaults (können im Wizard überschrieben werden)
    label_signer: "Maurizio Rogait",
    label_share: "60",
    feature_share: "40",
    management_share: "30",
    creator_share: "70",
    management_name: "Straßenwert",
    vertragsbeginn: "",
    vertragslaufzeit: "12 Monate",
    verlaengerung: "12 Monate",
    kuendigungsfrist: "3 Monate",
    ort: "Berlin",
    distributor: "MusicHub",
    song_version: "Original",
    release_type: "Single",
    textanteil_artist: "",
    textanteil_feature: "",
    kompanteil_artist: "",
    kompanteil_feature: "",
    sonstige_urheber: "",
    song_title: "",
    release_date: "",
    isrc: "",
    upc: "",
    feature_role: "",
    feature_time: "",
    feature_lyrics: "",
  };
  if (artist) {
    Object.assign(base, {
      artist_full_name: [artist.first_name, artist.last_name].filter(Boolean).join(" ").trim() || artist.artist_name,
      artist_name: artist.artist_name || "",
      artist_email: artist.email || "",
      artist_phone: artist.phone || "",
      artist_address: artist.address || "",
      artist_zip_city: artist.zip_city || "",
      artist_birthdate: artist.birthdate ? new Date(artist.birthdate).toLocaleDateString("de-DE") : "",
      artist_tax_id: artist.tax_id || "",
      artist_iban: artist.iban || "",
    });
  }
  if (customer) {
    Object.assign(base, {
      customer_name: customer.name || "",
      customer_company: customer.company || "",
      customer_email: customer.email || "",
      customer_phone: customer.phone || "",
      customer_address: customer.address || "",
      customer_zip_city: customer.zip_city || "",
      customer_birthdate: customer.birthdate ? new Date(customer.birthdate).toLocaleDateString("de-DE") : "",
    });
  }
  if (external) {
    Object.assign(base, {
      external_name: external.full_name || "",
      external_full_name: external.full_name || "",
      external_relation: external.relation || "",
      external_email: external.email || "",
      external_phone: external.phone || "",
      external_address: external.address || "",
      external_birthdate: external.birthdate ? new Date(external.birthdate).toLocaleDateString("de-DE") : "",
      customer_name: external.full_name || "",
      customer_email: external.email || "",
      customer_phone: external.phone || "",
      customer_address: external.address || "",
      customer_zip_city: external.zip_city || external.address || "",
      customer_birthdate: external.birthdate ? new Date(external.birthdate).toLocaleDateString("de-DE") : "",
    });
  }
  if (feature) {
    Object.assign(base, {
      feature_full_name: [feature.first_name, feature.last_name].filter(Boolean).join(" ").trim() || feature.artist_name,
      feature_artist_name: feature.artist_name || "",
      feature_name: feature.artist_name || "",
      feature_email: feature.email || "",
      feature_phone: feature.phone || "",
      feature_address: feature.address || "",
      feature_zip_city: feature.zip_city || "",
      feature_birthdate: feature.birthdate ? new Date(feature.birthdate).toLocaleDateString("de-DE") : "",
      feature_tax_id: feature.tax_id || "",
      feature_iban: feature.iban || "",
      feature_commission_percent: String(feature.commission_percent ?? 40),
      // Wenn im Feature-Datensatz ein Anteil gepflegt ist, diesen als Default-Split verwenden.
      feature_share: String(feature.commission_percent ?? 40),
      label_share: String(100 - Number(feature.commission_percent ?? 40)),
    });
  }
  if (creator) {
    Object.assign(base, {
      creator_full_name: [creator.first_name, creator.last_name].filter(Boolean).join(" ").trim() || creator.creator_name,
      creator_name: creator.creator_name || "",
      creator_email: creator.email || "",
      creator_phone: creator.phone || "",
      creator_address: creator.address || "",
      creator_zip_city: creator.zip_city || "",
      creator_birthdate: creator.birthdate ? new Date(creator.birthdate).toLocaleDateString("de-DE") : "",
      creator_tax_id: creator.tax_id || "",
      creator_iban: creator.iban || "",
      creator_commission_percent: String(creator.commission_percent ?? 30),
      management_share: String(creator.commission_percent ?? 30),
      creator_share: String(100 - Number(creator.commission_percent ?? 30)),
      management_name: "Straßenwert",
    });
  }
  return { ...base, ...custom };
}

// ═══════════════════════════════════════════════════════════════
// PDF Generation — Premium Straßenwert Design
// ═══════════════════════════════════════════════════════════════

export async function markdownToPdf(markdown, title = "Vertrag", signatures = []) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const marginX = 22;
  const bannerH = 40;
  const marginTop = bannerH + 16; // Tighter content start below banner
  const marginBottom = 26;
  const contentW = pageW - 2 * marginX;
  let y = marginTop;
  let sigIndex = 0; // counter for signature blocks encountered

  const logoData = await loadLogoDataUrl();

  const RED = [128, 24, 38];         // Bordeaux red banner
  const RED_ACCENT = [255, 0, 51];   // bright red accent
  const INK = [24, 24, 28];
  const MUTED = [130, 130, 140];
  const SOFT_BG = [232, 232, 236];   // light gray for § circles

  const drawBanner = () => {
    // Bordeaux-Banner mit Logo mittig
    pdf.setFillColor(...RED);
    pdf.rect(0, 0, pageW, bannerH, "F");
    if (logoData) {
      try {
        const logoW = 80;
        const logoH = 32;
        pdf.addImage(logoData, "PNG", (pageW - logoW) / 2, (bannerH - logoH) / 2, logoW, logoH);
      } catch (e) { /* ignore logo errors */ }
    }
  };

  const drawSubHeader = () => {
    // "STRASSENWERT · Vertrag" left, date right
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(...INK);
    pdf.text("STRASSENWERT", marginX, bannerH + 9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...MUTED);
    pdf.text(" · Vertrag", marginX + pdf.getTextWidth("STRASSENWERT"), bannerH + 9);
    pdf.setTextColor(...INK);
    pdf.text(new Date().toLocaleDateString("de-DE"), pageW - marginX, bannerH + 9, { align: "right" });
  };

  const drawIcon = (kind, cx, cy, size = 3) => {
    pdf.setDrawColor(...MUTED);
    pdf.setLineWidth(0.35);
    if (kind === "globe") {
      pdf.circle(cx, cy, size, "S");
      // vertical meridian
      pdf.line(cx, cy - size, cx, cy + size);
      // equator
      pdf.line(cx - size, cy, cx + size, cy);
      // side arcs (approximated via ellipse)
      pdf.ellipse(cx, cy, size * 0.55, size, "S");
    } else if (kind === "mail") {
      const w = size * 2, h = size * 1.4;
      pdf.rect(cx - w / 2, cy - h / 2, w, h, "S");
      // flap
      pdf.line(cx - w / 2, cy - h / 2, cx, cy + 0.2);
      pdf.line(cx + w / 2, cy - h / 2, cx, cy + 0.2);
    } else if (kind === "cam") {
      const w = size * 2, h = size * 1.6;
      pdf.rect(cx - w / 2, cy - h / 2, w, h, "S");
      pdf.circle(cx, cy + 0.1, size * 0.55, "S");
      // small dot
      pdf.setFillColor(...MUTED);
      pdf.circle(cx + w / 2 - 0.8, cy - h / 2 + 0.8, 0.35, "F");
    }
    pdf.setLineWidth(0.2);
  };

  const drawFooter = (page, total) => {
    const footerY = pageH - 14;
    pdf.setDrawColor(220, 220, 224);
    pdf.setLineWidth(0.25);
    pdf.line(marginX, footerY - 6, pageW - marginX, footerY - 6);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.8);
    pdf.setTextColor(...MUTED);

    const items = [
      { icon: "globe", text: "www.strassenwert.com" },
      { icon: "mail",  text: "management@strassenwert.de" },
    ];
    const cellW = contentW / items.length;
    items.forEach((it, i) => {
      const cellCenter = marginX + cellW * (i + 0.5);
      const textW = pdf.getTextWidth(it.text);
      const iconX = cellCenter - textW / 2 - 4;
      drawIcon(it.icon, iconX, footerY - 1, 2.4);
      pdf.setTextColor(...MUTED);
      pdf.text(it.text, cellCenter + 2, footerY);
      // vertical divider (except after last)
      if (i < items.length - 1) {
        const dx = marginX + cellW * (i + 1);
        pdf.setDrawColor(220, 220, 224);
        pdf.line(dx, footerY - 4, dx, footerY + 2);
      }
    });

    pdf.setFontSize(6.5);
    pdf.text(`Seite ${page} / ${total}`, pageW - marginX, footerY + 6, { align: "right" });
  };

  const ensureSpace = (need = 6) => {
    if (y + need > pageH - marginBottom) {
      pdf.addPage();
      drawBanner();
      drawSubHeader();
      y = marginTop;
    }
  };

  const writeParagraph = (rawText, opts = {}) => {
    const { size = 10.5, indent = 0, spacingAfter = 3, color = INK } = opts;
    pdf.setFontSize(size);
    // Strip italic markers _word_ only when they wrap actual letters (NOT underscore-lines)
    const cleanedText = rawText.replace(/(^|[^_A-Za-z0-9])_([A-Za-zÄÖÜäöüß0-9][^_\n]*?[A-Za-zÄÖÜäöüß0-9])_(?=[^_A-Za-z0-9]|$)/g, "$1$2");
    const runs = [];
    let rest = cleanedText;
    const boldRe = /\*\*(.+?)\*\*/;
    while (rest.length) {
      const m = rest.match(boldRe);
      if (!m) { runs.push({ text: rest, bold: false }); break; }
      if (m.index > 0) runs.push({ text: rest.slice(0, m.index), bold: false });
      runs.push({ text: m[1], bold: true });
      rest = rest.slice(m.index + m[0].length);
    }
    let cursorX = marginX + indent;
    const maxX = pageW - marginX;
    const lineHeight = size * 0.4 + 1.6;
    for (const run of runs) {
      pdf.setFont("helvetica", run.bold ? "bold" : "normal");
      pdf.setTextColor(...color);
      const words = run.text.split(/(\s+)/);
      for (const w of words) {
        if (!w) continue;
        const width = pdf.getTextWidth(w);
        if (cursorX + width > maxX) {
          y += lineHeight;
          ensureSpace(lineHeight);
          cursorX = marginX + indent;
          if (/^\s+$/.test(w)) continue;
        }
        ensureSpace(lineHeight);
        pdf.text(w, cursorX, y);
        cursorX += width;
      }
    }
    y += lineHeight + spacingAfter;
  };

  // Parse markdown into blocks
  const rawLines = markdown.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let buffer = [];
  const flush = () => {
    if (buffer.length) { blocks.push({ type: "p", text: buffer.join(" ") }); buffer = []; }
  };
  const RED_LABEL_RE = /^(zwischen|und|–\s*nachfolgend[^–]*–|- nachfolgend[^-]*-)$/i;

  for (const line of rawLines) {
    const t = line.trim();
    if (!t) { flush(); continue; }
    if (/^---+$/.test(t)) { flush(); blocks.push({ type: "hr" }); continue; }
    if (/^_{5,}/.test(t)) {
      flush();
      const captionMatch = t.match(/^_+\s*(.*)$/);
      const caption = captionMatch ? captionMatch[1].trim() : "";
      blocks.push({ type: "sig", caption });
      continue;
    }
    if (/^\(Unterschrift/.test(t)) {
      const last = blocks[blocks.length - 1];
      if (last && last.type === "sig" && !last.caption) {
        last.caption = t.replace(/^\(|\)$/g, "");
        continue;
      }
    }
    if (RED_LABEL_RE.test(t)) { flush(); blocks.push({ type: "redlabel", text: t }); continue; }
    if (/^# /.test(t)) { flush(); blocks.push({ type: "h1", text: t.slice(2).trim() }); continue; }
    if (/^## /.test(t)) {
      flush();
      const raw = t.slice(3).trim();
      const m = raw.match(/^§\s*(\d+)\s+(.+)$/);
      if (m) blocks.push({ type: "h2", num: m[1], text: m[2] });
      else blocks.push({ type: "h2", num: null, text: raw });
      continue;
    }
    if (/^### /.test(t)) { flush(); blocks.push({ type: "h3", text: t.slice(4).trim() }); continue; }
    if (/^- /.test(t)) { flush(); blocks.push({ type: "li", text: t.slice(2).trim() }); continue; }
    if (/^([0-9]+)\. /.test(t)) {
      flush();
      const m = t.match(/^([0-9]+)\.\s*(.+)$/);
      blocks.push({ type: "oli", num: m[1], text: m[2] });
      continue;
    }
    buffer.push(t);
  }
  flush();

  // Render
  drawBanner();
  drawSubHeader();
  y = marginTop;

  for (const b of blocks) {
    if (b.type === "h1") {
      ensureSpace(24);
      y += 4;
      pdf.setFont("helvetica", "bold");
      pdf.setCharSpace(-0.4);
      // Auto-Shrink: reduziere Font-Size, bis der Titel in eine Zeile passt
      const title = b.text.toUpperCase();
      let fs = 28;
      pdf.setFontSize(fs);
      while (pdf.getTextWidth(title) > contentW && fs > 14) {
        fs -= 1;
        pdf.setFontSize(fs);
      }
      // Falls immer noch zu breit (extrem langer Titel) → wrap
      pdf.setTextColor(...INK);
      const lines = pdf.splitTextToSize(title, contentW);
      const lineH = fs * 0.42 + 2;
      for (const l of lines) { ensureSpace(lineH + 2); pdf.text(l, marginX, y); y += lineH; }
      pdf.setCharSpace(0);
      pdf.setDrawColor(...RED_ACCENT);
      pdf.setLineWidth(1.4);
      pdf.line(marginX, y + 0.5, marginX + 60, y + 0.5);
      pdf.setLineWidth(0.2);
      y += 10;
    } else if (b.type === "h2") {
      ensureSpace(16);
      y += 5;
      const circleR = 5.5;
      const circleX = marginX + circleR;
      const circleY = y - 1.5;
      // Light gray filled circle
      pdf.setFillColor(...SOFT_BG);
      pdf.circle(circleX, circleY, circleR, "F");
      // Dark § symbol
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.setTextColor(...INK);
      pdf.text("§", circleX, circleY + 1.8, { align: "center" });
      // Section number + title after circle
      pdf.setTextColor(...INK);
      pdf.setFontSize(13);
      let titleX = marginX + 15;
      if (b.num) {
        pdf.text(`§ ${b.num}`, titleX, y);
        titleX += pdf.getTextWidth(`§ ${b.num}  `);
      }
      const lines = pdf.splitTextToSize(b.text.toUpperCase(), pageW - marginX - titleX);
      for (let i = 0; i < lines.length; i++) {
        ensureSpace(7);
        pdf.text(lines[i], titleX, y + (i === 0 ? 0 : 7));
      }
      y += Math.max(9, lines.length * 7) + 4;
    } else if (b.type === "redlabel") {
      ensureSpace(8);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.setTextColor(...RED_ACCENT);
      pdf.text(b.text, marginX, y);
      y += 7;
    } else if (b.type === "oli") {
      ensureSpace(8);
      // Red numbered circle
      const cr = 3;
      const cx = marginX + cr;
      const cy = y - 1.5;
      pdf.setFillColor(...RED_ACCENT);
      pdf.circle(cx, cy, cr, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.5);
      pdf.setTextColor(255, 255, 255);
      pdf.text(String(b.num || "•"), cx, cy + 0.9, { align: "center" });
      writeParagraph(b.text, { indent: 8, spacingAfter: 1.5 });
    } else if (b.type === "h3") {
      ensureSpace(9);
      y += 2;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10.5);
      pdf.setTextColor(...INK);
      const lines = pdf.splitTextToSize(b.text, contentW);
      for (const l of lines) { ensureSpace(6); pdf.text(l, marginX, y); y += 6; }
      y += 1;
    } else if (b.type === "hr") {
      ensureSpace(6);
      pdf.setDrawColor(220, 220, 224);
      pdf.line(marginX, y, pageW - marginX, y);
      y += 5;
    } else if (b.type === "sig") {
      // Signature block: wide line + caption below with breathing room
      ensureSpace(38);
      y += 4;
      const lineW = 78; // mm

      // Signatur-Zuordnung: 1) sequenziell per Index, 2) falls Index >= sigs.length
      // (z.B. weitere Anlage-Blöcke) → Fallback per Caption-Match: nutze die
      // Signatur, deren signer_name oder signer_label im caption vorkommt.
      let sig = signatures[sigIndex];
      if ((!sig || !sig.signature_png) && b.caption) {
        const cap = b.caption.toUpperCase();
        const matched = signatures.find((s) => {
          if (!s || !s.signature_png) return false;
          const name = (s.signer_name || "").toUpperCase();
          const label = (s.signer_label || "").toUpperCase();
          const role = (s.signer_role || "").toUpperCase();
          return (name && cap.includes(name)) ||
                 (label && cap.includes(label)) ||
                 (role && cap.includes(role));
        });
        if (matched) sig = matched;
      }
      sigIndex++;

      if (sig && sig.signature_png) {
        // Draw signature PNG above the line
        try {
          const imgH = 16;
          const imgW = lineW;
          pdf.addImage(sig.signature_png, "PNG", marginX, y - imgH + 2, imgW, imgH);
        } catch (e) { /* ignore image errors */ }
      } else {
        // Empty space for handwritten signature
        y += 8;
      }

      // Signature line
      pdf.setDrawColor(...INK);
      pdf.setLineWidth(0.4);
      pdf.line(marginX, y + 2, marginX + lineW, y + 2);
      pdf.setLineWidth(0.2);
      y += 6;

      if (b.caption) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8.5);
        pdf.setTextColor(...MUTED);
        pdf.text(b.caption, marginX, y);
      }

      // Signer name + date under caption (if signed)
      if (sig && sig.signer_name) {
        y += 4;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.setTextColor(...INK);
        pdf.text(sig.signer_name, marginX, y);
        if (sig.signed_at) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(7.5);
          pdf.setTextColor(...MUTED);
          pdf.text(
            "Digital signiert am " + new Date(sig.signed_at).toLocaleString("de-DE"),
            marginX,
            y + 4
          );
          y += 4;
        }
      }
      y += 8;
    } else if (b.type === "li") {
      ensureSpace(6);
      // Red small circle bullet
      pdf.setFillColor(...RED_ACCENT);
      pdf.circle(marginX + 3, y - 1.5, 1.2, "F");
      writeParagraph(b.text, { indent: 7, spacingAfter: 1 });
    } else {
      writeParagraph(b.text, { spacingAfter: 3 });
    }
  }

  // Footers
  const total = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    drawFooter(i, total);
  }

  return pdf;
}

// ═══════════════════════════════════════════════════════════════
// Save Contract → Storage + Contracts-Table + Activity
// (KEIN Ordner-Auto-Create — Verträge landen im CRM per artist_id/customer_id)
// ═══════════════════════════════════════════════════════════════

export async function generateAndArchiveContract({
  template,
  values,
  title,
  artist_id = null,
  customer_id = null,
  external_id = null,
  feature_id = null,
  creator_id = null,
  category,
}) {
  // 1) Render markdown
  const rendered = renderTemplate(template.body_markdown, values);

  // 2) Build PDF
  const pdf = await markdownToPdf(rendered, title);
  const blob = pdf.output("blob");
  const filename = `${title.replace(/[^a-zA-Z0-9._-]/g, "_")}.pdf`;

  // 3) Upload to Storage — path is namespaced by contact
  const timestamp = Date.now();
  const scopeId = artist_id || customer_id || external_id || feature_id || creator_id || "misc";
  const storagePath = `contracts/${scopeId}/${timestamp}-${filename}`;
  const { error: upErr } = await supabase.storage.from("drive-files").upload(storagePath, blob, {
    contentType: "application/pdf",
    upsert: false,
  });
  if (upErr) throw upErr;

  const now = new Date();
  const fileMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  // 4) Insert into drive_contracts
  const insertRow = {
    template_id: template.id,
    title,
    category: category || template.category,
    artist_id,
    customer_id,
    external_id,
    body_markdown: rendered,
    placeholders: values,
    status: "draft",
    storage_path: storagePath,
    contract_month: fileMonth,
  };
  // feature_id / creator_id nur setzen, wenn vorhanden — die Spalten kommen aus PATCHes
  if (feature_id) insertRow.feature_id = feature_id;
  if (creator_id) insertRow.creator_id = creator_id;

  const { data: contractRow, error: cErr } = await supabase
    .from("drive_contracts")
    .insert([insertRow])
    .select()
    .single();
  if (cErr) throw cErr;

  await logActivity("create_contract", { type: "contract", id: contractRow.id, name: title });

  // 5) Client download
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return { contract: contractRow };
}

// ═══════════════════════════════════════════════════════════════
// Fetchers
// ═══════════════════════════════════════════════════════════════

export async function fetchTemplates() {
  const { data, error } = await supabase.from("drive_contract_templates").select("*").order("name").order("created_at", { ascending: false });
  if (error) throw error;
  // De-dupe by (name + category) — newest wins
  const seen = new Set();
  const out = [];
  for (const t of data || []) {
    const key = `${(t.name || "").trim().toLowerCase()}|${t.category || ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

// De-duplicate contracts that share the same logical identity
// (title + artist_id + customer_id + template_id). Keeps the newest row.
// Data is expected to be ordered by created_at DESC — the first occurrence wins.
export function dedupeContracts(rows) {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const key = [
      (r.title || "").trim().toLowerCase(),
      r.artist_id || "",
      r.customer_id || "",
      r.external_id || "",
      r.feature_id || "",
      r.template_id || "",
    ].join("|");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

export async function fetchContracts() {
  const { data, error } = await supabase
    .from("drive_contracts")
    .select("*, drive_artists(artist_name), drive_customers(name), drive_externals(full_name), drive_features(artist_name), drive_creators(creator_name)")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return dedupeContracts(data);
}

export async function saveTemplate(payload) {
  const { id, ...rest } = payload;
  if (id) {
    const { data, error } = await supabase
      .from("drive_contract_templates")
      .update({ ...rest, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    return data;
  }
  const { data, error } = await supabase
    .from("drive_contract_templates")
    .insert([rest])
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteTemplate(id) {
  const { error } = await supabase.from("drive_contract_templates").delete().eq("id", id);
  if (error) throw error;
}

// ═══════════════════════════════════════════════════════════════
// FINALIZE SIGNED CONTRACT
// Bauet PDF neu mit allen eingebetteten Unterschriften und speichert
// unter signed_storage_path. Idempotent — kann mehrfach aufgerufen werden.
// ═══════════════════════════════════════════════════════════════
export async function finalizeSignedContract(contractId) {
  // 1) Contract holen
  const { data: contract, error: cErr } = await supabase
    .from("drive_contracts")
    .select("*")
    .eq("id", contractId)
    .single();
  if (cErr) throw cErr;

  // 2) Signaturen sortiert nach Erstellzeit
  const { data: sigs, error: sErr } = await supabase
    .from("drive_signatures")
    .select("*")
    .eq("contract_id", contractId)
    .order("created_at", { ascending: true });
  if (sErr) throw sErr;

  // Alle unterschrieben?
  const allSigned = sigs && sigs.length > 0 && sigs.every((s) => s.status === "signed" && s.signature_png);
  if (!allSigned) {
    throw new Error("Noch nicht alle Parteien haben unterschrieben.");
  }

  // 3) PDF neu bauen mit Signaturen
  const pdf = await markdownToPdf(contract.body_markdown, contract.title, sigs);
  const blob = pdf.output("blob");
  const filename = `${(contract.title || "Vertrag").replace(/[^a-zA-Z0-9._-]/g, "_")}-SIGNED.pdf`;

  // 4) Upload — separater Pfad, damit die Originaldatei erhalten bleibt
  const timestamp = Date.now();
  const scopeId = contract.artist_id || contract.customer_id || contract.external_id || contract.feature_id || contract.creator_id || "misc";
  const signedPath = `contracts/${scopeId}/${timestamp}-${filename}`;
  const { error: upErr } = await supabase.storage.from("drive-files").upload(signedPath, blob, {
    contentType: "application/pdf",
    upsert: false,
  });
  if (upErr) throw upErr;

  // 5) DB updaten
  const { error: updErr } = await supabase
    .from("drive_contracts")
    .update({
      signed_storage_path: signedPath,
      finalized_at: new Date().toISOString(),
      status: "signed",
    })
    .eq("id", contractId);
  if (updErr) throw updErr;

  await logActivity("finalize_contract", { type: "contract", id: contractId, name: contract.title });

  return { signedPath, blob };
}

// Prüfen ob Vertrag komplett signiert ist (und ggf. noch nicht finalisiert)
export async function contractIsFullySigned(contractId) {
  const { data: sigs, error } = await supabase
    .from("drive_signatures")
    .select("status, signature_png")
    .eq("contract_id", contractId);
  if (error) throw error;
  return sigs && sigs.length > 0 && sigs.every((s) => s.status === "signed" && s.signature_png);
}
